// ─── Builder Data Model ───────────────────────────────────────────────────────

export type ElementType =
  | 'section' |'container' |'div' |'heading' |'paragraph' |'text' |'button' |'image' |'link' |'spacer' |'divider' |'flex-container' |'grid-container';

export type ViewportMode = 'desktop' | 'tablet' | 'mobile-landscape' | 'mobile-portrait';

export type StyleUnit = 'px' | '%' | 'rem' | 'em' | 'vw' | 'vh' | 'auto';

export interface ElementStyles {
  // Layout
  display?: string;
  position?: string;
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  zIndex?: string;
  overflow?: string;
  // Size
  width?: string;
  height?: string;
  minWidth?: string;
  maxWidth?: string;
  minHeight?: string;
  maxHeight?: string;
  // Flex
  flexDirection?: string;
  flexWrap?: string;
  justifyContent?: string;
  alignItems?: string;
  alignContent?: string;
  gap?: string;
  rowGap?: string;
  columnGap?: string;
  // Grid
  gridTemplateColumns?: string;
  gridTemplateRows?: string;
  gridGap?: string;
  justifyItems?: string;
  alignSelf?: string;
  // Spacing
  padding?: string;
  paddingTop?: string;
  paddingRight?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  margin?: string;
  marginTop?: string;
  marginRight?: string;
  marginBottom?: string;
  marginLeft?: string;
  // Typography
  fontSize?: string;
  fontWeight?: string;
  fontFamily?: string;
  lineHeight?: string;
  letterSpacing?: string;
  textAlign?: string;
  textTransform?: string;
  textDecoration?: string;
  color?: string;
  // Background
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  backgroundRepeat?: string;
  backgroundGradient?: string;
  // Border
  border?: string;
  borderTop?: string;
  borderRight?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRadius?: string;
  borderTopLeftRadius?: string;
  borderTopRightRadius?: string;
  borderBottomRightRadius?: string;
  borderBottomLeftRadius?: string;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  // Shadow
  boxShadow?: string;
  // Effects
  opacity?: string;
  cursor?: string;
  // Transition (for interactions)
  transition?: string;
  transform?: string;
  // Custom
  [key: string]: string | undefined;
}

export interface ElementAttributes {
  href?: string;
  target?: string;
  src?: string;
  alt?: string;
  placeholder?: string;
  type?: string;
  [key: string]: string | undefined;
}

// ─── CSS Class System ─────────────────────────────────────────────────────────
export interface CSSClass {
  id: string;
  name: string;
  styles: ElementStyles;
  // Breakpoint overrides for the class
  breakpointStyles?: Partial<Record<ViewportMode, ElementStyles>>;
}

// ─── Breakpoint Styles ────────────────────────────────────────────────────────
export type BreakpointStyles = Partial<Record<ViewportMode, ElementStyles>>;

// ─── Element State Styles ─────────────────────────────────────────────────────
export type ElementStateType = 'normal' | 'hover' | 'focus' | 'active';

export interface ElementStateStyles {
  normal?: ElementStyles;
  hover?: ElementStyles;
  focus?: ElementStyles;
  active?: ElementStyles;
}

// ─── Interactions ─────────────────────────────────────────────────────────────
export type InteractionTrigger = 'pageLoad' | 'hover' | 'click' | 'scrollIntoView' | 'mouseMove';
export type AnimationType = 'fade' | 'slide' | 'scale' | 'rotate' | 'move' | 'resize' | 'opacity' | 'color';
export type EasingType = 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'linear' | 'bounce' | 'spring';
export type DirectionType = 'up' | 'down' | 'left' | 'right' | 'in' | 'out';

export interface Interaction {
  id: string;
  trigger: InteractionTrigger;
  animation: AnimationType;
  duration: number; // ms
  delay: number; // ms
  easing: EasingType;
  direction?: DirectionType;
  value?: string; // target value (e.g. color, opacity value)
  enabled: boolean;
}

// ─── Design Tokens ────────────────────────────────────────────────────────────
export type TokenCategory = 'colors' | 'fonts' | 'spacing' | 'sizes';

export interface DesignToken {
  id: string;
  name: string;
  value: string;
  category: TokenCategory;
  description?: string;
}

export interface DesignTokens {
  colors: DesignToken[];
  fonts: DesignToken[];
  spacing: DesignToken[];
  sizes: DesignToken[];
}

export const DEFAULT_DESIGN_TOKENS: DesignTokens = {
  colors: [
    { id: 'token-primary', name: 'Primary Color', value: '#2563eb', category: 'colors' },
    { id: 'token-secondary', name: 'Secondary Color', value: '#7c3aed', category: 'colors' },
    { id: 'token-accent', name: 'Accent Color', value: '#f59e0b', category: 'colors' },
    { id: 'token-text', name: 'Text Color', value: '#1a1a1a', category: 'colors' },
    { id: 'token-bg', name: 'Background', value: '#ffffff', category: 'colors' },
  ],
  fonts: [
    { id: 'token-body-font', name: 'Body Font', value: 'Inter, sans-serif', category: 'fonts' },
    { id: 'token-heading-font', name: 'Heading Font', value: 'Inter, sans-serif', category: 'fonts' },
  ],
  spacing: [
    { id: 'token-xs', name: 'XS Spacing', value: '4px', category: 'spacing' },
    { id: 'token-sm', name: 'SM Spacing', value: '8px', category: 'spacing' },
    { id: 'token-md', name: 'MD Spacing', value: '16px', category: 'spacing' },
    { id: 'token-lg', name: 'LG Spacing', value: '32px', category: 'spacing' },
    { id: 'token-xl', name: 'XL Spacing', value: '64px', category: 'spacing' },
  ],
  sizes: [
    { id: 'token-container', name: 'Container Width', value: '1200px', category: 'sizes' },
    { id: 'token-header-h', name: 'Header Height', value: '64px', category: 'sizes' },
  ],
};

// ─── Visibility per breakpoint ────────────────────────────────────────────────
export interface BreakpointVisibility {
  desktop?: boolean;
  tablet?: boolean;
  mobileLandscape?: boolean;
  mobilePortrait?: boolean;
}

// ─── Page SEO Settings ────────────────────────────────────────────────────────
export interface PageSEO {
  slug: string;
  title: string;
  metaDescription: string;
  ogImage: string;
  // Extended SEO
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  robots?: string; // e.g. "index,follow"
  keywords?: string;
}

export interface BuilderPage {
  id: string;
  name: string;
  isHomepage?: boolean;
  seo?: PageSEO;
  elements: Record<string, BuilderElement>;
  rootChildren: string[];
  cssClasses?: Record<string, CSSClass>;
}

// ─── Reusable Components ──────────────────────────────────────────────────────
export interface BuilderComponent {
  id: string;
  name: string;
  category: string;
  description?: string;
  elements: Record<string, BuilderElement>;
  rootChildren: string[];
  thumbnail?: string;
  createdAt: number;
  updatedAt: number;
}

// ─── Asset Manager ────────────────────────────────────────────────────────────
export type AssetType = 'image' | 'svg' | 'video' | 'file';

export interface BuilderAsset {
  id: string;
  name: string;
  type: AssetType;
  url: string;
  size?: number;
  width?: number;
  height?: number;
  mimeType?: string;
  uploadedAt: number;
}

// ─── Published Version ────────────────────────────────────────────────────────
export interface PublishedVersion {
  id: string;
  version: number;
  publishedAt: number;
  publishedBy?: string;
  html: string; // generated HTML per page keyed by pageId
  pages: Record<string, string>; // pageId -> html
  sitemapXml?: string;
  notes?: string;
}

// ─── Custom Domain ────────────────────────────────────────────────────────────
export interface CustomDomain {
  domain: string;
  status: 'pending' | 'active' | 'error';
  verificationToken?: string;
  addedAt: number;
}

// ─── Project (Dashboard) ──────────────────────────────────────────────────────
export interface BuilderProject {
  id: string;
  name: string;
  thumbnail?: string;
  pages: BuilderPage[];
  components: BuilderComponent[];
  assets: BuilderAsset[];
  published: boolean;
  publishedVersion?: PublishedVersion;
  draftVersion?: number;
  publishHistory?: PublishedVersion[];
  customDomain?: CustomDomain;
  createdAt: number;
  updatedAt: number;
  designTokens?: DesignTokens;
  // Global SEO / site settings
  siteSeo?: {
    siteTitle: string;
    siteDescription: string;
    siteUrl: string;
    ogImage: string;
    robots: string;
    generateSitemap: boolean;
    favicon?: string;
  };
}

// ─── Templates ────────────────────────────────────────────────────────────────
export type TemplateCategory = 'Business' | 'School' | 'Restaurant' | 'Hotel' | 'Portfolio' | 'Real Estate' | 'Event';

export interface BuilderTemplate {
  id: string;
  name: string;
  category: TemplateCategory;
  description: string;
  thumbnail: string;
  pages: Omit<BuilderPage, 'id'>[];
}

// ─── History ──────────────────────────────────────────────────────────────────
export interface HistoryEntry {
  elements: Record<string, BuilderElement>;
  rootChildren: string[];
  cssClasses?: Record<string, CSSClass>;
}

// ─── Builder State ────────────────────────────────────────────────────────────
export interface BuilderState {
  // Multi-page
  pages: BuilderPage[];
  activePageId: string;
  // Current page (derived from pages + activePageId)
  page: BuilderPage;
  // Selection
  selectedId: string | null;
  hoveredId: string | null;
  viewport: ViewportMode;
  zoom: number;
  history: HistoryEntry[];
  historyIndex: number;
  saveStatus: 'idle' | 'saving' | 'saved';
  activePanel: 'elements' | 'navigator' | 'pages' | 'components' | 'assets' | 'templates' | 'tokens';
  dragState: DragState | null;
  styleActiveBreakpoint: ViewportMode;
  // Components & Assets
  components: BuilderComponent[];
  assets: BuilderAsset[];
  // New
  clipboard: BuilderElement | null; // copied element
  designTokens: DesignTokens;
}

export interface DragState {
  sourceId: string | null;
  elementType?: ElementType;
  dropTargetId: string | null;
  dropPosition: 'before' | 'after' | 'inside' | null;
  assetId?: string; // dragging asset onto canvas
}

// ─── Element Defaults ─────────────────────────────────────────────────────────

export const ELEMENT_DEFAULTS: Record<ElementType, { content: string; styles: ElementStyles; attributes: ElementAttributes }> = {
  section: {
    content: '',
    styles: { padding: '40px 20px', backgroundColor: '#ffffff', minHeight: '80px', width: '100%', display: 'block' },
    attributes: {},
  },
  container: {
    content: '',
    styles: { maxWidth: '1200px', margin: '0 auto', padding: '0 20px', minHeight: '40px', display: 'block' },
    attributes: {},
  },
  div: {
    content: '',
    styles: { padding: '8px', minHeight: '40px', display: 'block' },
    attributes: {},
  },
  heading: {
    content: 'Heading Text',
    styles: { fontSize: '32px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 16px 0', lineHeight: '1.2', display: 'block' },
    attributes: {},
  },
  paragraph: {
    content: 'This is a paragraph. Click to edit this text.',
    styles: { fontSize: '16px', color: '#444444', lineHeight: '1.6', margin: '0 0 16px 0', display: 'block' },
    attributes: {},
  },
  text: {
    content: 'Text element',
    styles: { fontSize: '14px', color: '#333333', lineHeight: '1.5', display: 'inline-block' },
    attributes: {},
  },
  button: {
    content: 'Click Me',
    styles: { padding: '12px 24px', backgroundColor: '#2563eb', color: '#ffffff', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'inline-block', border: 'none' },
    attributes: {},
  },
  image: {
    content: '',
    styles: { width: '100%', height: 'auto', display: 'block', borderRadius: '4px' },
    attributes: { src: 'https://placehold.co/600x400/e2e8f0/94a3b8?text=Image', alt: 'Image' },
  },
  link: {
    content: 'Click here',
    styles: { color: '#2563eb', fontSize: '14px', textDecoration: 'underline', cursor: 'pointer', display: 'inline-block' },
    attributes: { href: '#', target: '_self' },
  },
  spacer: {
    content: '',
    styles: { height: '40px', display: 'block', width: '100%' },
    attributes: {},
  },
  divider: {
    content: '',
    styles: { borderTop: '1px solid #e2e8f0', margin: '16px 0', display: 'block', width: '100%' },
    attributes: {},
  },
  'flex-container': {
    content: '',
    styles: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '16px', padding: '16px', minHeight: '60px', alignItems: 'flex-start' },
    attributes: {},
  },
  'grid-container': {
    content: '',
    styles: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', padding: '16px', minHeight: '60px' },
    attributes: {},
  },
};

export const ELEMENT_LABELS: Record<ElementType, string> = {
  section: 'Section',
  container: 'Container',
  div: 'Div',
  heading: 'Heading',
  paragraph: 'Paragraph',
  text: 'Text',
  button: 'Button',
  image: 'Image',
  link: 'Link',
  spacer: 'Spacer',
  divider: 'Divider',
  'flex-container': 'Flex Container',
  'grid-container': 'Grid Container',
};

export const CONTAINER_TYPES: ElementType[] = ['section', 'container', 'div', 'flex-container', 'grid-container'];

export function isContainer(type: ElementType): boolean {
  return CONTAINER_TYPES.includes(type);
}

export function generateId(): string {
  return `el-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

export function generateClassId(): string {
  return `cls-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

export function generatePageId(): string {
  return `page-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

export function generateComponentId(): string {
  return `comp-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

export function generateAssetId(): string {
  return `asset-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

export function generateInteractionId(): string {
  return `int-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

export function generateTokenId(): string {
  return `token-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

export function createElement(type: ElementType, parentId: string | null = null): BuilderElement {
  const defaults = ELEMENT_DEFAULTS[type];
  return {
    id: generateId(),
    type,
    parentId,
    children: [],
    content: defaults.content,
    label: ELEMENT_LABELS[type],
    styles: { ...defaults.styles },
    breakpointStyles: {},
    cssClasses: [],
    attributes: { ...defaults.attributes },
    hidden: false,
    locked: false,
  };
}

export const VIEWPORT_WIDTHS: Record<ViewportMode, number> = {
  desktop: 1280,
  tablet: 768,
  'mobile-landscape': 568,
  'mobile-portrait': 375,
};

export const VIEWPORT_LABELS: Record<ViewportMode, string> = {
  desktop: 'Desktop',
  tablet: 'Tablet',
  'mobile-landscape': 'Mobile L',
  'mobile-portrait': 'Mobile P',
};

// ─── Style Resolution ─────────────────────────────────────────────────────────
// Resolves the effective styles for an element at a given breakpoint
// Desktop styles are the base; smaller breakpoints inherit and override
export const VIEWPORT_ORDER: ViewportMode[] = ['desktop', 'tablet', 'mobile-landscape', 'mobile-portrait'];

export function resolveStyles(el: BuilderElement, viewport: ViewportMode, cssClasses?: Record<string, CSSClass>): ElementStyles {
  // Start with base (desktop) styles
  let resolved: ElementStyles = { ...el.styles };

  // Apply CSS class base styles
  if (el.cssClasses && cssClasses) {
    for (const classId of el.cssClasses) {
      const cls = cssClasses[classId];
      if (cls) {
        resolved = { ...resolved, ...cls.styles };
      }
    }
  }

  // Apply breakpoint overrides in order (desktop → current viewport)
  const viewportIdx = VIEWPORT_ORDER.indexOf(viewport);
  for (let i = 1; i <= viewportIdx; i++) {
    const bp = VIEWPORT_ORDER[i];
    const bpStyles = el.breakpointStyles?.[bp];
    if (bpStyles) {
      resolved = { ...resolved, ...bpStyles };
    }
    // Also apply class breakpoint overrides
    if (el.cssClasses && cssClasses) {
      for (const classId of el.cssClasses) {
        const cls = cssClasses[classId];
        if (cls?.breakpointStyles?.[bp]) {
          resolved = { ...resolved, ...cls.breakpointStyles[bp] };
        }
      }
    }
  }

  return resolved;
}

// ─── Default Templates ────────────────────────────────────────────────────────
function makeEl(type: ElementType, content: string, styles: Partial<ElementStyles> = {}, children: BuilderElement[] = []): BuilderElement {
  const base = createElement(type, null);
  const childIds = children.map(c => c.id);
  return { ...base, content, styles: { ...base.styles, ...styles }, children: childIds };
}

function buildTree(elements: BuilderElement[]): { elements: Record<string, BuilderElement>; rootChildren: string[] } {
  const map: Record<string, BuilderElement> = {};
  for (const el of elements) map[el.id] = el;
  return { elements: map, rootChildren: elements.map(e => e.id) };
}

export function createTemplatePages(category: TemplateCategory): BuilderPage[] {
  const pageId = generatePageId();

  const makeHeroSection = (title: string, subtitle: string, btnText: string, bg: string): BuilderElement[] => {
    const section = createElement('section', null);
    section.styles = { ...section.styles, backgroundColor: bg, padding: '80px 20px', textAlign: 'center' };
    const container = createElement('container', section.id);
    container.parentId = section.id;
    const heading = createElement('heading', container.id);
    heading.content = title;
    heading.styles = { ...heading.styles, fontSize: '48px', color: '#1a1a1a', marginBottom: '16px' };
    heading.parentId = container.id;
    const para = createElement('paragraph', container.id);
    para.content = subtitle;
    para.styles = { ...para.styles, fontSize: '18px', color: '#555', marginBottom: '32px' };
    para.parentId = container.id;
    const btn = createElement('button', container.id);
    btn.content = btnText;
    btn.parentId = container.id;
    container.children = [heading.id, para.id, btn.id];
    section.children = [container.id];
    return [section, container, heading, para, btn];
  };

  const makeNavSection = (links: string[]): BuilderElement[] => {
    const section = createElement('section', null);
    section.styles = { ...section.styles, backgroundColor: '#1a1a2e', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' };
    const logo = createElement('heading', section.id);
    logo.content = links[0] || 'Brand';
    logo.styles = { ...logo.styles, fontSize: '20px', color: '#fff', margin: '0', fontWeight: '700' };
    logo.parentId = section.id;
    const nav = createElement('flex-container', section.id);
    nav.styles = { ...nav.styles, display: 'flex', gap: '24px', padding: '0', minHeight: 'auto', background: 'transparent' };
    nav.parentId = section.id;
    const navLinks = links.slice(1).map(l => {
      const link = createElement('link', nav.id);
      link.content = l;
      link.styles = { ...link.styles, color: '#ccc', textDecoration: 'none', fontSize: '14px' };
      link.parentId = nav.id;
      return link;
    });
    nav.children = navLinks.map(l => l.id);
    section.children = [logo.id, nav.id];
    return [section, logo, nav, ...navLinks];
  };

  const makeFooter = (text: string): BuilderElement[] => {
    const section = createElement('section', null);
    section.styles = { ...section.styles, backgroundColor: '#1a1a2e', padding: '32px 20px', textAlign: 'center' };
    const para = createElement('paragraph', section.id);
    para.content = text;
    para.styles = { ...para.styles, color: '#aaa', fontSize: '14px', margin: '0' };
    para.parentId = section.id;
    section.children = [para.id];
    return [section, para];
  };

  const templates: Record<TemplateCategory, BuilderPage[]> = {
    Business: [{
      id: pageId, name: 'Home', isHomepage: true,
      seo: { slug: '/', title: 'Business — Home', metaDescription: 'Professional business website', ogImage: '' },
      cssClasses: {},
      ...(() => {
        const nav = makeNavSection(['BizCorp', 'Home', 'About', 'Services', 'Contact']);
        const hero = makeHeroSection('Grow Your Business', 'Professional solutions for modern companies.', 'Get Started', '#f0f4ff');
        const footer = makeFooter('© 2026 BizCorp. All rights reserved.');
        const all = [...nav, ...hero, ...footer];
        const map: Record<string, BuilderElement> = {};
        for (const el of all) map[el.id] = el;
        return { elements: map, rootChildren: [nav[0].id, hero[0].id, footer[0].id] };
      })(),
    }],
    School: [{
      id: pageId, name: 'Home', isHomepage: true,
      seo: { slug: '/', title: 'School — Home', metaDescription: 'Welcome to our school', ogImage: '' },
      cssClasses: {},
      ...(() => {
        const nav = makeNavSection(['EduAcademy', 'Home', 'Programs', 'Admissions', 'Contact']);
        const hero = makeHeroSection('Shaping Future Leaders', 'Excellence in education since 1990.', 'Apply Now', '#f0fff4');
        const footer = makeFooter('© 2026 EduAcademy. All rights reserved.');
        const all = [...nav, ...hero, ...footer];
        const map: Record<string, BuilderElement> = {};
        for (const el of all) map[el.id] = el;
        return { elements: map, rootChildren: [nav[0].id, hero[0].id, footer[0].id] };
      })(),
    }],
    Restaurant: [{
      id: pageId, name: 'Home', isHomepage: true,
      seo: { slug: '/', title: 'Restaurant — Home', metaDescription: 'Fine dining experience', ogImage: '' },
      cssClasses: {},
      ...(() => {
        const nav = makeNavSection(['La Maison', 'Home', 'Menu', 'Reservations', 'About']);
        const hero = makeHeroSection('Exquisite Dining Experience', 'Farm-to-table cuisine in an elegant setting.', 'Reserve a Table', '#fff8f0');
        const footer = makeFooter('© 2026 La Maison. All rights reserved.');
        const all = [...nav, ...hero, ...footer];
        const map: Record<string, BuilderElement> = {};
        for (const el of all) map[el.id] = el;
        return { elements: map, rootChildren: [nav[0].id, hero[0].id, footer[0].id] };
      })(),
    }],
    Hotel: [{
      id: pageId, name: 'Home', isHomepage: true,
      seo: { slug: '/', title: 'Hotel — Home', metaDescription: 'Luxury hotel experience', ogImage: '' },
      cssClasses: {},
      ...(() => {
        const nav = makeNavSection(['Grand Hotel', 'Home', 'Rooms', 'Amenities', 'Book Now']);
        const hero = makeHeroSection('Luxury Redefined', 'Experience world-class hospitality.', 'Book Your Stay', '#f5f0ff');
        const footer = makeFooter('© 2026 Grand Hotel. All rights reserved.');
        const all = [...nav, ...hero, ...footer];
        const map: Record<string, BuilderElement> = {};
        for (const el of all) map[el.id] = el;
        return { elements: map, rootChildren: [nav[0].id, hero[0].id, footer[0].id] };
      })(),
    }],
    Portfolio: [{
      id: pageId, name: 'Home', isHomepage: true,
      seo: { slug: '/', title: 'Portfolio — Home', metaDescription: 'Creative portfolio', ogImage: '' },
      cssClasses: {},
      ...(() => {
        const nav = makeNavSection(['Jane Doe', 'Work', 'About', 'Blog', 'Contact']);
        const hero = makeHeroSection('Creative Designer & Developer', 'Crafting beautiful digital experiences.', 'View My Work', '#fafafa');
        const footer = makeFooter('© 2026 Jane Doe. All rights reserved.');
        const all = [...nav, ...hero, ...footer];
        const map: Record<string, BuilderElement> = {};
        for (const el of all) map[el.id] = el;
        return { elements: map, rootChildren: [nav[0].id, hero[0].id, footer[0].id] };
      })(),
    }],
    'Real Estate': [{
      id: pageId, name: 'Home', isHomepage: true,
      seo: { slug: '/', title: 'Real Estate — Home', metaDescription: 'Find your dream home', ogImage: '' },
      cssClasses: {},
      ...(() => {
        const nav = makeNavSection(['PropFinder', 'Buy', 'Rent', 'Sell', 'Agents']);
        const hero = makeHeroSection('Find Your Dream Home', 'Thousands of listings in your area.', 'Search Properties', '#f0f8ff');
        const footer = makeFooter('© 2026 PropFinder. All rights reserved.');
        const all = [...nav, ...hero, ...footer];
        const map: Record<string, BuilderElement> = {};
        for (const el of all) map[el.id] = el;
        return { elements: map, rootChildren: [nav[0].id, hero[0].id, footer[0].id] };
      })(),
    }],
    Event: [{
      id: pageId, name: 'Home', isHomepage: true,
      seo: { slug: '/', title: 'Event — Home', metaDescription: 'Join our amazing event', ogImage: '' },
      cssClasses: {},
      ...(() => {
        const nav = makeNavSection(['EventPro', 'Home', 'Schedule', 'Speakers', 'Register']);
        const hero = makeHeroSection('The Premier Tech Conference 2026', 'Join 5,000+ professionals for 3 days of learning.', 'Register Now', '#fff0f5');
        const footer = makeFooter('© 2026 EventPro. All rights reserved.');
        const all = [...nav, ...hero, ...footer];
        const map: Record<string, BuilderElement> = {};
        for (const el of all) map[el.id] = el;
        return { elements: map, rootChildren: [nav[0].id, hero[0].id, footer[0].id] };
      })(),
    }],
  };

  return templates[category];
}

// ─── Default Component Library ────────────────────────────────────────────────
export const DEFAULT_COMPONENT_LIBRARY: Omit<BuilderComponent, 'id' | 'createdAt' | 'updatedAt'>[] = [
  { name: 'Navbar', category: 'Navigation', description: 'Responsive navigation bar', elements: {}, rootChildren: [] },
  { name: 'Footer', category: 'Navigation', description: 'Site footer with links', elements: {}, rootChildren: [] },
  { name: 'Hero', category: 'Sections', description: 'Full-width hero section', elements: {}, rootChildren: [] },
  { name: 'Card', category: 'Content', description: 'Content card with image', elements: {}, rootChildren: [] },
  { name: 'Testimonial', category: 'Content', description: 'Customer testimonial block', elements: {}, rootChildren: [] },
  { name: 'Pricing Card', category: 'Commerce', description: 'Pricing tier card', elements: {}, rootChildren: [] },
  { name: 'Contact Form', category: 'Forms', description: 'Contact form with fields', elements: {}, rootChildren: [] },
  { name: 'Button', category: 'Interactive', description: 'Styled CTA button', elements: {}, rootChildren: [] },
];

function BuilderElement(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: BuilderElement is not implemented yet.', args);
  return null;
}

export { BuilderElement };